"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateMapFeatureFromInterview = void 0;
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
/** TODO This file's functions should be dropped in favor of using the
 * de-serialized interview object, when the admin's Interview objects are ready
 * to be used with sufficient defaults in evolution */
const get_1 = __importDefault(require("lodash/get"));
const odSurveyHelper = __importStar(require("evolution-common/lib/services/odSurvey/helpers"));
const SurveyGeographyUtils_1 = require("evolution-common/lib/services/geodata/SurveyGeographyUtils");
/**
 * Generates GeoJSON feature collections for places and trips from an interview,
 * with deduplication of places that share the same activity type and coordinates.
 *
 * Deduplication is particularly important because home and usual places (work, school, etc.)
 * are typically duplicated multiple times within a journey:
 * - The home place appears both as "response.home" and as visited places in journeys
 * - Usual places like work or school appear multiple times across different trips and journeys
 * - Multiple household members may visit the same places
 * Without deduplication, the map would show multiple overlapping markers for the same location.
 *
 * @param interview - The interview data containing survey responses
 * @param options - Configuration options
 * @param options.activePlacePath - Path to the currently active/selected place (e.g., "response.home" or "response.household.persons.uuid.journeys.uuid.visitedPlaces.uuid")
 * @param options.activeTripUuid - UUID of the currently active/selected trip
 *
 * @returns An object containing:
 * - `placesCollection`: GeoJSON FeatureCollection of Point features representing unique places
 * - `tripsCollection`: GeoJSON FeatureCollection of LineString features representing trips as Bezier curves
 * - `pathToUniqueKeyMap`: Map that maps original place paths to their deduplicated unique keys.
 *   This is used to maintain selection state when places are deduplicated. For example:
 *   - Original paths: "response.household.persons.A.journeys.1.visitedPlaces.X" and "response.household.persons.B.journeys.2.visitedPlaces.Y"
 *   - If both represent the same activity at the same coordinates, they map to the same unique key: "workNotUsual_-73.12345_45.67890"
 *   - Unique keys are formatted as: `${activity}_${roundedLng}_${roundedLat}` (coordinates rounded to 5 decimal places)
 *   - This allows the UI to know which deduplicated place corresponds to the originally selected place path
 */
const generateMapFeatureFromInterview = (
// Admin interviews carry the unserialized audits payload (see SurveyAdmin.ts),
// used here only for person colors.
interview, { activePlacePath, activeTripUuid }) => {
    var _a, _b, _c, _d;
    const placesCollection = {
        type: 'FeatureCollection',
        features: []
    };
    const tripsCollection = {
        type: 'FeatureCollection',
        features: []
    };
    const response = interview.response;
    // Use raw response data for trip generation (as it was working before)
    const persons = odSurveyHelper.getPersonsArray({ interview });
    // Person colors from deserialized survey objects (surveyObjectsAndAudits.household.members).
    // Color lookup still falls back to _color on the raw response person when the map misses.
    const deserializedPersons = (_c = (_b = (_a = interview.surveyObjectsAndAudits) === null || _a === void 0 ? void 0 : _a.household) === null || _b === void 0 ? void 0 : _b.members) !== null && _c !== void 0 ? _c : [];
    const personColorMap = new Map();
    // Build a map of person UUID to color from deserialized objects
    deserializedPersons.forEach((person) => {
        if (person._uuid && person._color) {
            personColorMap.set(person._uuid, person._color);
        }
    });
    const roundedCoordinatesPairsCount = {};
    const coordinatesByVisitedPlaceUuid = {};
    // Map to track unique places by activity and coordinates
    const uniquePlacesMap = new Map();
    // Map from original path to unique key for selection mapping
    const pathToUniqueKeyMap = new Map();
    // Add the visited places and trips to the places and trips collections for each person
    for (const person of persons) {
        // Prefer deserialized color map; fall back to raw person _color, then default.
        const personColor = personColorMap.get(person._uuid) || person._color || '#000000';
        const personPath = `response.household.persons.${person._uuid}`;
        const journeys = odSurveyHelper.getJourneysArray({ person });
        for (const journey of journeys) {
            const visitedPlaces = odSurveyHelper.getVisitedPlacesArray({ journey });
            const trips = odSurveyHelper.getTripsArray({ journey });
            // Add the visited places to the places collection
            for (const visitedPlace of visitedPlaces) {
                const visitedPlacePath = `${personPath}.journeys.${journey._uuid}.visitedPlaces.${visitedPlace._uuid}`;
                const geography = odSurveyHelper.getVisitedPlaceGeography({ visitedPlace, interview, person });
                if (geography) {
                    const coordinates = (0, get_1.default)(geography, 'geometry.coordinates', null);
                    coordinatesByVisitedPlaceUuid[visitedPlace._uuid] = coordinates;
                    const isActive = visitedPlacePath === activePlacePath;
                    // Create unique key based on activity and rounded coordinates
                    if (!coordinates)
                        continue; // Skip if no coordinates
                    const roundedLng = Math.round(coordinates[0] * 100000) / 100000;
                    const roundedLat = Math.round(coordinates[1] * 100000) / 100000;
                    const uniqueKey = `${visitedPlace.activity}_${roundedLng}_${roundedLat}`;
                    // Map this path to the unique key
                    pathToUniqueKeyMap.set(visitedPlacePath, uniqueKey);
                    if (uniquePlacesMap.has(uniqueKey)) {
                        // Add path to existing entry and update active state
                        const existingEntry = uniquePlacesMap.get(uniqueKey);
                        existingEntry.paths.push(visitedPlacePath);
                        existingEntry.isAnyActive = existingEntry.isAnyActive || isActive;
                    }
                    else {
                        // Create new entry
                        const place = {
                            type: 'Feature',
                            geometry: geography.geometry,
                            properties: {
                                path: visitedPlacePath, // Use visited place path, not coordinates path
                                activity: visitedPlace.activity,
                                lastAction: (0, get_1.default)(visitedPlace, 'geography.properties.lastAction', '?'),
                                active: isActive,
                                name: visitedPlace.name,
                                personUuid: person._uuid,
                                visitedPlaceUuid: visitedPlace._uuid
                            }
                        };
                        uniquePlacesMap.set(uniqueKey, {
                            place,
                            paths: [visitedPlacePath],
                            isAnyActive: isActive
                        });
                    }
                }
            }
            // Add the trips to the trips collection for this person
            for (const trip of trips) {
                const originPlaceUuid = trip._originVisitedPlaceUuid;
                const destinationPlaceUuid = trip._destinationVisitedPlaceUuid;
                if (!originPlaceUuid || !destinationPlaceUuid) {
                    continue;
                }
                const originCoordinates = coordinatesByVisitedPlaceUuid[originPlaceUuid];
                const destinationCoordinates = coordinatesByVisitedPlaceUuid[destinationPlaceUuid];
                // TODO Handle junction geographies here
                if (originCoordinates && destinationCoordinates) {
                    const roundedCoordinatesPair = (Math.round(originCoordinates[0] * 1000) / 1000).toString() +
                        ',' +
                        (Math.round(destinationCoordinates[0] * 1000) / 1000).toString();
                    if (!roundedCoordinatesPairsCount[roundedCoordinatesPair]) {
                        roundedCoordinatesPairsCount[roundedCoordinatesPair] = 1;
                    }
                    else {
                        roundedCoordinatesPairsCount[roundedCoordinatesPair]++;
                    }
                    const superposedSequence = roundedCoordinatesPairsCount[roundedCoordinatesPair];
                    const tripCurve = (0, SurveyGeographyUtils_1.pointsToBezierCurve)([
                        { type: 'Point', coordinates: originCoordinates },
                        { type: 'Point', coordinates: destinationCoordinates }
                    ], {
                        superposedSequence,
                        additionalProperties: {
                            personUuid: person._uuid,
                            tripUuid: trip._uuid,
                            active: activeTripUuid === trip._uuid,
                            personColor: personColor
                        }
                    });
                    tripsCollection.features.push(tripCurve);
                }
            }
        }
    }
    // Add the main home geography (will be deduplicated if there are home visited places)
    const homeGeography = ((_d = response.home) === null || _d === void 0 ? void 0 : _d.geography) ? response.home.geography : null;
    if (homeGeography) {
        const homePath = 'response.home';
        const isHomeActive = homePath === activePlacePath;
        // Create unique key for home place
        const coordinates = homeGeography.geometry.coordinates;
        const roundedLng = Math.round(coordinates[0] * 100000) / 100000;
        const roundedLat = Math.round(coordinates[1] * 100000) / 100000;
        const uniqueKey = `home_${roundedLng}_${roundedLat}`;
        // Map this path to the unique key
        pathToUniqueKeyMap.set(homePath, uniqueKey);
        if (uniquePlacesMap.has(uniqueKey)) {
            // Add path to existing entry and update active state
            const existingEntry = uniquePlacesMap.get(uniqueKey);
            existingEntry.paths.push(homePath);
            existingEntry.isAnyActive = existingEntry.isAnyActive || isHomeActive;
        }
        else {
            // Create new entry
            const place = {
                type: 'Feature',
                geometry: homeGeography.geometry,
                properties: {
                    path: homePath,
                    activity: 'home',
                    active: isHomeActive
                }
            };
            uniquePlacesMap.set(uniqueKey, {
                place,
                paths: [homePath],
                isAnyActive: isHomeActive
            });
        }
    }
    // Add deduplicated places to the collection
    for (const [, entry] of uniquePlacesMap) {
        // Update the active state based on whether any duplicate is active
        entry.place.properties.active = entry.isAnyActive;
        placesCollection.features.push(entry.place);
    }
    return { placesCollection, tripsCollection, pathToUniqueKeyMap };
};
exports.generateMapFeatureFromInterview = generateMapFeatureFromInterview;
//# sourceMappingURL=odSurveyAdminHelper.js.map